import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
} from '@/lib/firebase/firestore'
import type { CompanyMemberDocument } from '@/types'
import type { MemberFormValues } from '../types'

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const membersCol = () => typedCollection<CompanyMemberDocument>('company')

type MemberCreatePayload = Omit<CompanyMemberDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminCompanyService = {
  async createMember(data: MemberFormValues): Promise<string> {
    const slug = toSlug(data.name)
    const payload: MemberCreatePayload = {
      name: data.name,
      slug,
      bio: data.bio,
      photoUrl: '',
      squarePhotoUrl: '',
      portraitPhotoUrl: '',
      roles: data.roles,
      positionIds: data.positionIds,
      isActive: data.isActive,
      isPublished: data.isPublished,
      seasonNumbers: data.seasonNumbers,
      order: data.order,
      socialLinks: {
        instagram: data.socialLinks.instagram || '',
        website: data.socialLinks.website || '',
      },
      availableForHire: data.availableForHire ?? false,
      ...(data.email ? { email: data.email } : {}),
      ...(data.photoCredit ? { photoCredit: data.photoCredit } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(membersCol(), payload as unknown as CompanyMemberDocument)
    return ref.id
  },

  async updateMember(id: string, data: Partial<MemberFormValues>): Promise<void> {
    const ref = typedDoc<CompanyMemberDocument>('company', id)
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
    if (data.name !== undefined) {
      updates.name = data.name
      updates.slug = toSlug(data.name)
    }
    if (data.bio !== undefined) updates.bio = data.bio
    if (data.roles !== undefined) updates.roles = data.roles
    if (data.positionIds !== undefined) updates.positionIds = data.positionIds
    if (data.isActive !== undefined) updates.isActive = data.isActive
    if (data.isPublished !== undefined) updates.isPublished = data.isPublished
    if (data.seasonNumbers !== undefined) updates.seasonNumbers = data.seasonNumbers
    if (data.order !== undefined) updates.order = data.order
    if (data.socialLinks !== undefined) {
      updates.socialLinks = {
        instagram: data.socialLinks.instagram || '',
        website: data.socialLinks.website || '',
      }
    }
    if (data.hourlyRate !== undefined) updates.hourlyRate = data.hourlyRate
    if (data.productionSkills !== undefined) updates.productionSkills = data.productionSkills
    if (data.availableForHire !== undefined) updates.availableForHire = data.availableForHire
    if (data.email !== undefined) updates.email = data.email || null
    if (data.photoCredit !== undefined) updates.photoCredit = data.photoCredit || null
    await updateDocument(ref, updates as unknown as CompanyMemberDocument)
  },

  async deleteMember(id: string): Promise<void> {
    const ref = typedDoc<CompanyMemberDocument>('company', id)
    await deleteDocument(ref)
  },

  async getAllMembers(): Promise<Array<CompanyMemberDocument & { id: string }>> {
    return getCollection(membersCol(), orderBy('order', 'asc'))
  },
}
